import React, { useState, useEffect } from 'react';

/**
 * QuestionStandard Component
 * Used for: IST, CFIT, TIU, WPT, Tes Khusus, Potensi Inteligensi
 * Format: Multiple choice (A, B, C, D, E) with support for single choice & multiple select (allow_multiple)
 */
const QuestionStandard = ({
    question,
    answer,
    onAnswer,
    disabled = false
}) => {
    const isMultiple = Boolean(question.allow_multiple || question.is_multiple);

    // Normalize answer state (supports array or comma-separated string for multi-select)
    const getInitialSelected = () => {
        if (!answer) return isMultiple ? [] : null;
        if (Array.isArray(answer)) return answer;
        if (typeof answer === 'string') {
            if (isMultiple) {
                return answer.split(',').map(s => s.trim()).filter(Boolean);
            }
            return answer.trim();
        }
        return isMultiple ? [] : null;
    };

    const [selectedAnswer, setSelectedAnswer] = useState(getInitialSelected());

    useEffect(() => {
        setSelectedAnswer(getInitialSelected());
    }, [answer, question.id, isMultiple]);

    const handleAnswer = (value) => {
        if (disabled) return;

        if (isMultiple) {
            let currentList = Array.isArray(selectedAnswer) ? [...selectedAnswer] : [];
            if (currentList.includes(value)) {
                // Toggle off
                currentList = currentList.filter(item => item !== value);
            } else {
                // Toggle on
                currentList.push(value);
            }
            setSelectedAnswer(currentList);
            onAnswer(question.id, currentList.join(','));
        } else {
            setSelectedAnswer(value);
            onAnswer(question.id, value);
        }
    };

    // Robustly resolve options list (supports array, object {"A":"...", "B":"..."}, string, or mapping)
    const getOptionsList = () => {
        let rawOptions = question.pilihan || (question.mapping && question.mapping.options) || question.options;

        if (typeof rawOptions === 'string') {
            try {
                rawOptions = JSON.parse(rawOptions);
            } catch (e) {
                rawOptions = [];
            }
        }

        if (!rawOptions) return [];

        if (Array.isArray(rawOptions)) {
            return rawOptions.map((opt, idx) => {
                if (typeof opt === 'object' && opt !== null) {
                    return {
                        label: opt.label || String.fromCharCode(65 + idx),
                        text: opt.text || opt.value || ''
                    };
                }
                return {
                    label: String.fromCharCode(65 + idx),
                    text: opt
                };
            });
        }

        if (typeof rawOptions === 'object') {
            return Object.entries(rawOptions).map(([key, val]) => ({
                label: key,
                text: typeof val === 'object' && val !== null ? (val.text || val.value || '') : val
            }));
        }

        return [];
    };

    const optionsList = getOptionsList();

    return (
        <div className="question-standard">
            {/* Question Text */}
            <div className="question-text mb-4">
                <div
                    className="text-lg font-semibold"
                    dangerouslySetInnerHTML={{ __html: question.soal }}
                />
            </div>

            {/* Image Display */}
            {question.image_path && (
                <div className="question-image mb-4">
                    <img
                        src={`/storage/${question.image_path}`}
                        alt="Question illustration"
                        className="max-w-full h-auto rounded-lg shadow-md"
                        loading="lazy"
                        onError={(e) => {
                            e.target.style.display = 'none';
                        }}
                    />
                </div>
            )}

            {/* Multiple Choice Notice Banner */}
            {isMultiple && (
                <div className="alert alert-info py-2 px-3 mb-3 d-flex align-items-center" style={{ backgroundColor: '#eff6ff', borderColor: '#bfdbfe', color: '#1e40af', borderRadius: '10px' }}>
                    <i className="fas fa-check-double mr-2" style={{ fontSize: '1.2rem' }}></i>
                    <span style={{ fontWeight: 600 }}>Soal ini adalah Multiple Choice: Anda dapat memilih <strong>lebih dari satu jawaban</strong>.</span>
                </div>
            )}

            {/* Answer Options */}
            <div className="answer-options row mt-3 px-3">
                {optionsList.map((opt, index) => {
                    const optionLabel = opt.label;
                    const isSelected = isMultiple
                        ? (Array.isArray(selectedAnswer) && selectedAnswer.includes(optionLabel))
                        : (selectedAnswer === optionLabel);

                    return (
                        <div key={index} className="col-12 mb-2">
                            <button
                                type="button"
                                className="btn btn-lg w-100"
                                onClick={() => handleAnswer(optionLabel)}
                                disabled={disabled}
                                style={{
                                    fontSize: '1.1rem',
                                    padding: '0.75rem',
                                    borderStyle: 'solid',
                                    borderWidth: '2px',
                                    borderColor: isSelected ? (isMultiple ? '#10b981' : '#f56409') : '#dee2e6',
                                    backgroundColor: isSelected ? (isMultiple ? '#ecfdf5' : '#f564091a') : '#fff',
                                    color: '#212529',
                                    textAlign: 'left',
                                    display: 'flex',
                                    alignItems: 'center',
                                    cursor: disabled ? 'not-allowed' : 'pointer',
                                    transition: 'all 0.2s ease',
                                    boxShadow: isSelected ? '0 4px 12px rgba(0,0,0,0.06)' : 'none'
                                }}
                            >
                                <span
                                    style={{
                                        display: 'inline-flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        width: '40px',
                                        height: '40px',
                                        marginRight: '15px',
                                        borderRadius: '8px',
                                        fontSize: '1.3rem',
                                        fontWeight: '700',
                                        borderStyle: isMultiple ? 'solid' : 'dashed',
                                        borderWidth: '2px',
                                        borderColor: isSelected ? (isMultiple ? '#10b981' : '#f56409') : '#cbd5e1',
                                        backgroundColor: isSelected ? (isMultiple ? '#10b981' : '#f564094d') : '#f8fafc',
                                        color: isSelected ? '#ffffff' : '#64748b',
                                        flexShrink: 0,
                                        transition: 'all 0.2s ease'
                                    }}
                                >
                                    {isMultiple && isSelected ? '✓' : optionLabel}
                                </span>
                                <span style={{ flex: 1 }} dangerouslySetInnerHTML={{ __html: opt.text }} />
                            </button>
                        </div>
                    );
                })}
            </div>

            {/* Helper Text */}
            <div className="helper-text mt-4 text-sm text-gray-500">
                {isMultiple ? (
                    <p style={{ color: '#059669', fontWeight: 600 }}>
                        <i className="fas fa-info-circle mr-1"></i> Klik opsi untuk menambah atau membatalkan pilihan jawaban Anda.
                    </p>
                ) : (
                    <p>Pilih salah satu jawaban yang paling tepat</p>
                )}
            </div>
        </div>
    );
};

export default QuestionStandard;
